import React, {
    useState,
    useRef,
    useEffect,
    useCallback,
    useMemo,
} from "react";
import { ProgressLevelData } from "../OlympianCardV2";
import CheckIcon from "@mui/icons-material/Check";
import styles from "./ProgressLevel.module.scss";
import { Box, Typography } from "@mui/material";
import classNames from "classnames";

interface Tick {
    index: number;
    levelIndex: number;
    subTickIndex: number;
    isVisible: boolean;
    isCompleted: boolean;
}

const ProgressLevel: React.FC<{
    levels: any[];
    gameUserLevel: ProgressLevelData | null;
    completedLevels: number;
}> = ({ levels, gameUserLevel, completedLevels }) => {
    const isDesktop = window.innerWidth >= 1024;

    const totalLevels: number = levels.length;
    const slidesPerView: number = isDesktop ? 4.5 : 3.5;
    const spaceBetween: number = 10;
    const [currentLevelProgress, setCurrentLevelProgress] = useState<number>(0);
    const [tickPositions, setTickPositions] = useState<number[]>([]);
    const [currentProgressPercentage, setCurrentProgressPercentage] =
        useState<number>(0);
    const [isEndProgress, setIsEndProgress] = useState<boolean>(false);
    const [scrollPosition, setScrollPosition] = useState<number>(0);
    const [containerWidth, setContainerWidth] = useState<number>(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const progressBarRef = useRef<HTMLDivElement>(null);
    const isDragging = useRef<boolean>(false);
    const startX = useRef<number>(0);
    const scrollLeft = useRef<number>(0);

    const levelWidth: number =
        (containerWidth - spaceBetween * (slidesPerView - 1)) / slidesPerView;

    const getMilestonesPerLevel = (levelIndex: number): number => {
        if (levelIndex <= 1) return 1;
        if (levelIndex === 2) return 2;
        if (totalLevels - 1 === levelIndex) return 1;
        return 5;
    };

    const totalTicks: number = levels.reduce((acc, _, index) => {
        return acc + getMilestonesPerLevel(index);
    }, 0);

    const calculatePositionsAndProgress = useCallback(() => {
        if (!containerRef.current || !progressBarRef.current) return;

        const totalContentWidth: number =
            totalLevels * (levelWidth + spaceBetween) - spaceBetween;
        const translate: number = -scrollPosition;
        setIsEndProgress(
            scrollPosition >= totalContentWidth - containerWidth - 15
        );

        const tickPositionsArray: number[] = [];
        let tickIndex: number = 0;

        for (let levelIndex = 0; levelIndex < totalLevels; levelIndex++) {
            const initialPosition: number =
                levelIndex * (levelWidth + spaceBetween);
            const position: number =
                initialPosition + translate + levelWidth / 2;
            let positionPercent: number = (position / containerWidth) * 100;

            tickPositionsArray[tickIndex] = positionPercent;
            tickIndex++;

            const milestones: number = getMilestonesPerLevel(levelIndex);
            for (let subTick = 1; subTick < milestones; subTick++) {
                const subTickOffset: number =
                    (subTick / milestones) * (levelWidth + spaceBetween);
                const subPosition: number =
                    initialPosition +
                    subTickOffset +
                    translate +
                    levelWidth / 2;
                let subPositionPercent: number =
                    (subPosition / containerWidth) * 100;
                tickPositionsArray[tickIndex] = subPositionPercent;
                tickIndex++;
            }
        }

        let progressPercentage: number = 0;
        const lastCompletedTickIndex: number = Math.max(0, completedLevels - 1);
        const completedLevelPosition: number =
            lastCompletedTickIndex >= 0
                ? tickPositionsArray[lastCompletedTickIndex] || 0
                : 0;

        const currentLevelIndex: number = completedLevels;
        const milestonesInCurrentLevel: number =
            getMilestonesPerLevel(currentLevelIndex);
        const progressPerMilestone: number = 100 / milestonesInCurrentLevel;
        const completedMilestones: number = Math.floor(
            currentLevelProgress / progressPerMilestone
        );
        const remainingProgress: number =
            currentLevelProgress % progressPerMilestone;

        const currentLevelTickOffset: number = levels
            .slice(0, completedLevels)
            .reduce((acc, _, index) => acc + getMilestonesPerLevel(index), 0);
        const currentTickIndex: number =
            currentLevelTickOffset + completedMilestones;
        const nextTickIndex: number = currentTickIndex + 1;

        const currentTickPosition: number =
            tickPositionsArray[currentTickIndex] || 0;
        const nextTickPosition: number =
            tickPositionsArray[nextTickIndex] || currentTickPosition;

        const startPosition: number = tickPositionsArray[completedLevels] || 0;

        if (completedLevels > 0) {
            if (
                completedLevels >= 1
            ) {
                if (completedLevels > 1) {
                    progressPercentage = completedLevelPosition;
                    if (currentLevelProgress >= 0) {
                        const tickDistance: number =
                            nextTickPosition - currentTickPosition;
                        const additionalProgress: number =
                            (remainingProgress / progressPerMilestone) *
                            tickDistance;
                        progressPercentage =
                            currentTickPosition + additionalProgress;
                    }
                } else {
                    const tickDistance: number =
                        nextTickPosition - startPosition;
                    const additionalProgress: number =
                        (currentLevelProgress / 100) * tickDistance;
                    progressPercentage = startPosition + additionalProgress;
                }
            }
        } else if (completedLevels === 0 && currentLevelProgress > 0) {
            const tickDistance: number = nextTickPosition - startPosition;
            const additionalProgress: number =
                (currentLevelProgress / 100) * tickDistance;
            progressPercentage = startPosition + additionalProgress;
        }

        setTickPositions(tickPositionsArray);
        setCurrentProgressPercentage(
            isReachedMaxLevel ? 100 : progressPercentage
        );
    }, [
        completedLevels,
        currentLevelProgress,
        totalLevels,
        scrollPosition,
        containerWidth,
        levelWidth,
    ]);

    const visibleTicks: Tick[] = Array.from(
        { length: totalTicks },
        (_, index) => {
            let levelIndex: number = 0;
            let subTickIndex: number = 0;
            let currentTick: number = 0;

            for (let i = 0; i < totalLevels; i++) {
                const milestones: number = getMilestonesPerLevel(i);
                if (index >= currentTick && index < currentTick + milestones) {
                    levelIndex = i;
                    subTickIndex = index - currentTick;
                    break;
                }
                currentTick += milestones;
            }

            const tickPosition: number = tickPositions[index] || 0;
            const isVisible: boolean =
                tickPosition >= -30 && tickPosition <= 150;

            let isCompleted: boolean = false;
            if (levelIndex < completedLevels) {
                isCompleted = true;
            } else if (levelIndex === completedLevels) {
                const milestones: number = getMilestonesPerLevel(levelIndex);
                const progressPerMilestone: number = 100 / milestones;
                const completedMilestones: number = Math.floor(
                    currentLevelProgress / progressPerMilestone
                );
                isCompleted = subTickIndex <= completedMilestones;
            }

            return { index, levelIndex, subTickIndex, isVisible, isCompleted };
        }
    );

    const nextLevelIndex: number = Math.min(
        completedLevels + 1,
        totalLevels - 1
    );
    const nextLevelName: string =
        levels?.length > 0 ? levels[nextLevelIndex].name : "";
    const milestonesInCurrentLevel: number =
        getMilestonesPerLevel(completedLevels);

    const parseTimeToMinutes = (timeString: string): number => {
        if (!timeString) return 0;
        const [hours] = timeString
            .split(" ")
            .filter((part) => !isNaN(parseInt(part)));
        return parseInt(hours) * 60;
    };

    const totalMinutesForCurrentLevel: number = parseTimeToMinutes(
        levels?.length > 0 ? levels[completedLevels]?.time : ""
    );

    const isReachedMaxLevel = useMemo(() => {
        return completedLevels === totalLevels - 1;
    }, [totalLevels, completedLevels]);

    const handleScroll = (): void => {
        if (containerRef.current) {
            setScrollPosition(containerRef.current.scrollLeft);
        }
    };

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
        if (containerRef.current) {
            isDragging.current = true;
            startX.current = e.pageX - containerRef.current.offsetLeft;
            scrollLeft.current = containerRef.current.scrollLeft;
            containerRef.current.style.cursor = "grabbing";
        }
    };

    const handleMouseLeave = (): void => {
        isDragging.current = false;
        if (containerRef.current) {
            containerRef.current.style.cursor = "grab";
        }
    };

    const handleMouseUp = (): void => {
        isDragging.current = false;
        if (containerRef.current) {
            containerRef.current.style.cursor = "grab";
        }
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>): void => {
        if (!isDragging.current || !containerRef.current) return;
        e.preventDefault();
        const x: number = e.pageX - containerRef.current.offsetLeft;
        const walk: number = (x - startX.current) * 1.5;
        containerRef.current.scrollLeft = scrollLeft.current - walk;
    };

    const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>): void => {
        if (containerRef.current) {
            isDragging.current = true;
            startX.current =
                e.touches[0].pageX - containerRef.current.offsetLeft;
            scrollLeft.current = containerRef.current.scrollLeft;
        }
    };

    const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>): void => {
        if (!isDragging.current || !containerRef.current) return;
        const x: number = e.touches[0].pageX - containerRef.current.offsetLeft;
        const walk: number = (x - startX.current) * 1.5;
        containerRef.current.scrollLeft = scrollLeft.current - walk;
    };

    const handleTouchEnd = (): void => {
        isDragging.current = false;
    };

    const scrollToCompletedLevel = useCallback(() => {
        if (!containerRef.current || containerWidth <= 0) return;
      
        const container = containerRef.current;
        const maxScroll = container.scrollWidth - container.clientWidth;
      
        const levelIndex = Math.min(completedLevels, totalLevels - 1);
        const levelPosition = levelIndex * (levelWidth + spaceBetween);
      
        let scrollTarget = levelPosition - containerWidth / 2 + levelWidth / 2;
      
        scrollTarget = Math.max(0, Math.min(scrollTarget, maxScroll));
      
      
        container.scrollTo({
          left: scrollTarget,
          behavior: 'auto'
        });
      }, [completedLevels, containerWidth, levelWidth, totalLevels]);
      
    useEffect(() => {
        if (levels?.length > 0 && gameUserLevel) {
            setCurrentLevelProgress(gameUserLevel?.progress);
        }
    }, [levels, gameUserLevel]);

    useEffect(() => {
        const updateContainerWidth = (): void => {
            if (containerRef.current) {
                setContainerWidth(
                    containerRef.current.getBoundingClientRect().width
                );
            }
        };

        updateContainerWidth();
        window.addEventListener("resize", updateContainerWidth);
        return () => window.removeEventListener("resize", updateContainerWidth);
    }, []);

    useEffect(() => {
        calculatePositionsAndProgress();
        const container = containerRef.current;
        if (container) {
            container.addEventListener("scroll", handleScroll);
            return () => container.removeEventListener("scroll", handleScroll);
        }
    }, [calculatePositionsAndProgress]);

    useEffect(() => {
        if (containerWidth > 0) {
          scrollToCompletedLevel();
        }
      }, [containerWidth, scrollToCompletedLevel]);

    const athleteTickPosition: number = tickPositions[0] || 0;

    return (
        <div className={styles.app}>
            <div
                className={styles.levelsContainer}
                ref={containerRef}
                onMouseDown={handleMouseDown}
                onMouseLeave={handleMouseLeave}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                <div
                    className={styles.levelsWrapper}
                    style={{
                        width: `${
                            totalLevels * (levelWidth + spaceBetween) -
                            spaceBetween
                        }px`,
                    }}
                >
                    {levels.map((level, index) => (
                        <div
                            key={index}
                            className={`${styles.levelItem} ${
                                index <= completedLevels ? styles.completed : ""
                            }`}
                            style={{
                                width: `${levelWidth}px`,
                                marginRight: `${spaceBetween}px`,
                            }}
                        >
                            <Box
                                className={styles.icon}
                                sx={{
                                    width: {
                                        xs: "100px",
                                        lg: "128px",
                                    },
                                    height: {
                                        xs: "100px",
                                        lg: "128px",
                                    },
                                }}
                            >
                                <img
                                    src={`${
                                        index <= completedLevels
                                            ? level.icon
                                            : level.iconGray
                                    }`}
                                    alt="Aviron Achievement"
                                    loading="eager"
                                    draggable="false"
                                />
                            </Box>
                            <div className={styles.label}>
                                {level.name?.toUpperCase()}
                            </div>
                            {index !== 0 && (
                                <div className={styles.time}>{level.time}</div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className={styles.progressBarContainer} ref={progressBarRef}>
                <div
                    className={styles.progressBarBackground}
                    style={{
                        clipPath: `inset(0 ${
                            isEndProgress
                                ? isDesktop
                                    ? "10.5%"
                                    : "13.5%"
                                : "0%"
                        } 0 ${athleteTickPosition - 0}%)`,
                    }}
                >
                    <div
                        className={styles.progressBar}
                        style={{
                            width: `${
                                currentProgressPercentage < 0
                                    ? 0
                                    : currentProgressPercentage
                            }%`,
                        }}
                    >
                        <p
                            style={{
                                backgroundColor: "#fff",
                                width: "15px",
                                height: "15px",
                                borderRadius: "50%",
                                position: "absolute",
                                left: `${currentProgressPercentage - 0.5}%`,
                                top: "-2px",
                                margin: 0,
                            }}
                        ></p>
                    </div>
                </div>
                <div className={styles.ticksContainer}>
                    {visibleTicks.map(
                        ({
                            index,
                            levelIndex,
                            subTickIndex,
                            isVisible,
                            isCompleted,
                        }) => (
                            <div
                                key={index}
                                className={`${styles.tick} ${
                                    isVisible ? styles.visible : styles.hidden
                                } ${isCompleted ? styles.completed : ""} ${
                                    subTickIndex > 0 ? styles.subTick : ""
                                } ${
                                    levelIndex === totalLevels - 1 &&
                                    subTickIndex > 0
                                        ? styles.subTickEnd
                                        : ""
                                }
                                ${
                                    isCompleted && completedLevels > levelIndex
                                        ? styles.completedSubtick
                                        : ""
                                }
                                `}
                                style={{
                                    left: tickPositions[index]
                                        ? `${tickPositions[index]}%`
                                        : "0%",
                                }}
                            >
                                {isCompleted && isVisible && (
                                    <span className={styles.checkmark}>
                                        <CheckIcon
                                            sx={{
                                                fontSize: "16px",
                                            }}
                                        />
                                    </span>
                                )}
                            </div>
                        )
                    )}
                </div>
                <div
                    className={classNames({
                        [styles.progressLabel]:
                            completedLevels > 1 && !isReachedMaxLevel,
                        [styles.progressLabelMaxLevel]:
                            completedLevels > 1 && isReachedMaxLevel,
                        [styles.progressLabelShorter]:
                            completedLevels <= 1 &&
                            currentProgressPercentage > 0,
                        [styles.progressLabelFirstLevel]:
                            completedLevels === 0 &&
                            currentProgressPercentage < 16
                    })}
                    style={{
                        left: `${
                            currentProgressPercentage <= 16
                                ? window.innerWidth < 1024
                                    ? "27"
                                    : "20"
                                : currentProgressPercentage === 100
                                ? "81"
                                : currentProgressPercentage
                        }%`,
                        display: `${
                            (currentProgressPercentage === 0 &&
                                scrollPosition > 110) ||
                            (currentProgressPercentage === 100 &&
                                scrollPosition < 2550) ||
                            currentProgressPercentage < 0
                                ? "none"
                                : "block"
                        }`,
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: "GRIFTER",
                            fontWeight: 700,
                            fontSize: "20px",
                            letterSpacing: "0.2px",
                            textTransform: "uppercase",
                        }}
                    >
                        {currentLevelProgress}% TO {nextLevelName}
                    </Typography>
                    {Boolean(!isReachedMaxLevel) && (
                        <Typography
                            sx={{
                                fontWeight: 400,
                                fontSize: "16px",
                                fontFamily: "Work Sans",
                                marginTop: "8px",
                            }}
                        >
                            {gameUserLevel && gameUserLevel?.remaining.hour > 0
                                ? `${gameUserLevel?.remaining.hour} ${
                                      gameUserLevel?.remaining.hour > 1
                                          ? "hours"
                                          : "hour"
                                  } `
                                : ""}
                            {gameUserLevel
                                ? `${gameUserLevel?.remaining.minute} ${
                                      gameUserLevel?.remaining.minute > 1
                                          ? "minutes"
                                          : "minute"
                                  } `
                                : ""}{" "}
                            to Level Up
                        </Typography>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProgressLevel;
